import React, { useState, useEffect, useCallback, createContext, useContext, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import Home from './pages/Home';
import LandingRouter from './components/LandingRouter';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import { Toaster } from './components/ui/sonner';
import { WebRTCProvider } from './components/webrtc/LiveKitContext';
import { LanguageProvider } from './context/LanguageContext';
import SupportChatWidget from './components/SupportChatWidget';
import ActivityPopup from './components/ActivityPopup';
import SocialProofPopup from './components/SocialProofPopup';
import AppDownloadPopup from './components/AppDownloadPopup';
import ClassStartAlarm from './components/ClassStartAlarm';
import { installClientErrorReporter } from './utils/errorReporter';

// Wire the global JS error reporter as early as possible so we capture
// bootstrap-time errors (bundle parse, chunk load failures, etc.) too.
installClientErrorReporter();

// Retry wrapper for lazy imports - handles chunk loading failures after deployments
const lazyRetry = (importFn) => lazy(() =>
  importFn().catch(() => {
    // Chunk failed to load (likely stale cache after deploy) - reload once
    if (!sessionStorage.getItem('chunk_retry')) {
      sessionStorage.setItem('chunk_retry', '1');
      window.location.reload();
    }
    return importFn();
  })
);

// Lazy-loaded pages for faster initial load
const CourseDetail = lazyRetry(() => import('./pages/CourseDetail'));
const CoursePlayer = lazyRetry(() => import('./pages/CoursePlayer'));
const LiveClassDetail = lazyRetry(() => import('./pages/LiveClassDetail'));
const TeacherProfile = lazyRetry(() => import('./pages/TeacherProfile'));
const EbookReader = lazyRetry(() => import('./pages/EbookReader'));
const AITutor = lazyRetry(() => import('./pages/AITutor'));
const PublicLibraryReader = lazyRetry(() => import('./pages/PublicLibraryReader'));
const EbookDetail = lazyRetry(() => import('./pages/EbookDetail'));
const AudioRooms = lazyRetry(() => import('./pages/AudioRooms'));
const Reels = lazyRetry(() => import('./pages/Reels'));
const ExamTaking = lazyRetry(() => import('./pages/ExamTaking'));
const Dashboard = lazyRetry(() => import('./pages/Dashboard'));
const AllCourses = lazyRetry(() => import('./pages/AllCourses'));
const AllLiveClasses = lazyRetry(() => import('./pages/AllLiveClasses'));
const Leaderboard = lazyRetry(() => import('./pages/Leaderboard'));
const AllEbooks = lazyRetry(() => import('./pages/AllEbooks'));
const AllNotes = lazyRetry(() => import('./pages/AllNotes'));
const AllExams = lazyRetry(() => import('./pages/AllExams'));
const AllTeachers = lazyRetry(() => import('./pages/AllTeachers'));
const Cart = lazyRetry(() => import('./pages/Cart'));
const CheckoutPage = lazyRetry(() => import('./pages/CheckoutPage'));
const AdminDashboard = lazyRetry(() => import('./pages/admin/AdminDashboard'));
const TeacherPortal = lazyRetry(() => import('./pages/teacher/TeacherPortal'));
const SchoolAdminDashboard = lazyRetry(() => import('./pages/SchoolAdminDashboard'));
const TeacherRegister = lazy(() => import('./pages/TeacherRegister'));
const BookingSession = lazy(() => import('./pages/BookingSession'));
const NoteDetail = lazy(() => import('./pages/NoteDetail'));
const NoteReader = lazy(() => import('./pages/NoteReader'));
const StudentProfile = lazy(() => import('./pages/StudentProfile'));
const MyLibrary = lazyRetry(() => import('./pages/MyLibrary'));
const SessionRoom = lazy(() => import('./pages/SessionRoom'));
const SchoolPortal = lazy(() => import('./pages/SchoolPortal'));
const ForYouPage = lazy(() => import('./pages/ForYouPage'));
const RegisterSchool = lazy(() => import('./pages/RegisterSchool'));
const SimulationsPage = lazy(() => import('./pages/SimulationsPage'));
const RecordingPlayback = lazyRetry(() => import('./pages/RecordingPlayback'));
import SessionReminderPopup from './components/SessionReminderPopup';

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
import StudentOnboarding from './components/StudentOnboarding';
import { installAuthSession, refreshIfExpiringSoon, clearSession, endSession } from './lib/authSession';

export const API = `${BACKEND_URL}/api`;

export const AuthContext = createContext();
export const VirtualTeacherContext = createContext({ enabled: true, refresh: () => {} });

// Set up axios interceptor to ensure auth header is always present
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

installAuthSession();

// Handle 401 responses globally
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.warn('Authentication required or token expired');
    }
    return Promise.reject(error);
  }
);

// Analytics tracking hook
function AnalyticsTracker() {
  const location = useLocation();
  
  useEffect(() => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    const page = pathParts[0] || 'home';
    const pageId = pathParts[1] || null;
    
    axios.post(`${API}/analytics/track/page`, {
      page: page,
      page_id: pageId,
      referrer: document.referrer || null
    }).catch(() => {});
    
    // Always update last_active + device + IP-geo (GPS preferred if user has it)
    const sendHeartbeat = (coords) => {
      const body = {
        platform: 'web',
        app_version: 'web-1.0',
      };
      if (coords) {
        body.latitude = coords.latitude;
        body.longitude = coords.longitude;
      }
      axios.post(`${API}/analytics/heartbeat`, body).catch(() => {});
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          sendHeartbeat({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
          if (!sessionStorage.getItem('locationTracked')) {
            axios.post(`${API}/analytics/track/location`, {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              country: 'Tanzania',
            }).catch(() => {});
            sessionStorage.setItem('locationTracked', 'true');
          }
        },
        () => { sendHeartbeat(null); },  // permission denied → backend uses IP-geo
        { timeout: 4000, maximumAge: 600000 },
      );
    } else {
      sendHeartbeat(null);
    }
  }, [location.pathname]);

  // Heartbeat for active user tracking (every 60s)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const sendHeartbeat = () => {
      fetch(`${API}/admin/track-heartbeat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          platform: 'web',
          page: location.pathname,
          device: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
          user_agent: navigator.userAgent,
        })
      }).catch(() => {});
    };
    sendHeartbeat();
    const interval = setInterval(sendHeartbeat, 60000);
    return () => clearInterval(interval);
  }, [location.pathname]);
  
  return null;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vtEnabled, setVtEnabled] = useState(true);
  const [notifSettings, setNotifSettings] = useState({
    social_proof_enabled: true,
    download_popup_enabled: true,
    activity_popup_enabled: true,
    session_reminder_enabled: true,
  });

  // Fetch Virtual Teacher feature flag (public endpoint — works for anon too)
  const refreshVT = useCallback(() => {
    axios.get(`${API}/ai-tutor/settings`)
      .then((r) => setVtEnabled(r.data?.enabled !== false))
      .catch(() => setVtEnabled(true)); // fail-open only on network error
  }, []);
  useEffect(() => { refreshVT(); }, [user, refreshVT]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const loadMe = (retriesLeft) => refreshIfExpiringSoon()
        .then(() => axios.get(`${API}/auth/me`))
        .then(res => { setUser(res.data); setLoading(false); })
        .catch((err) => {
          if (err?.response?.status === 401) {
            clearSession();
            delete axios.defaults.headers.common['Authorization'];
          } else if (retriesLeft > 0) {
            setTimeout(() => loadMe(retriesLeft - 1), 3000);
            return;
          }
          setLoading(false);
        });
      loadMe(2);
    } else {
      setLoading(false);
    }
    // Fetch notification display settings
    fetch(`${API}/admin/notification-settings`).then(r => r.ok ? r.json() : null).then(d => { if (d) setNotifSettings(d); }).catch(() => {});
  }, []);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
  };

  const logout = () => {
    endSession();
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  useEffect(() => {
    const onExpired = () => {
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
    };
    window.addEventListener('auth:expired', onExpired);
    return () => window.removeEventListener('auth:expired', onExpired);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      <VirtualTeacherContext.Provider value={{ enabled: vtEnabled, refresh: refreshVT }}>
      <LanguageProvider>
      <WebRTCProvider>
        <BrowserRouter>
          <AnalyticsTracker />
          <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Landing page is admin-configurable via Settings → Layout Manager */}
            <Route path="/" element={<LandingRouter />} />
            <Route path="/home" element={<Home />} />
            <Route path="/login" element={!user ? <Login /> : (
              user.role === 'teacher' ? <Navigate to="/teacher/dashboard" /> : 
              user.role === 'admin' ? <Navigate to="/admin" /> : 
              <Navigate to="/" />
            )} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/teacher-register" element={<TeacherRegister />} />
            <Route path="/courses/:id" element={<CourseDetail />} />
            <Route path="/learn/course/:courseId" element={<CoursePlayer />} />
            <Route path="/live-classes/:id" element={<LiveClassDetail />} />
            <Route path="/all-courses" element={<AllCourses />} />
            <Route path="/live-classes" element={<AllLiveClasses />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/ebooks" element={<AllEbooks />} />
            <Route path="/schools/:schoolId" element={<SchoolPortal />} />
            <Route path="/for-you" element={user ? <ForYouPage /> : <Navigate to="/login" />} />
            <Route path="/register-school" element={user ? <RegisterSchool /> : <Navigate to="/login" />} />
            <Route path="/simulations" element={<SimulationsPage />} />
            <Route path="/tutor" element={user ? (vtEnabled || user.role === 'admin' ? <AITutor /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/tutor/:lessonId" element={user ? (vtEnabled || user.role === 'admin' ? <AITutor /> : <Navigate to="/" />) : <Navigate to="/login" />} />
            <Route path="/ebooks/:id" element={<EbookDetail />} />
            <Route path="/ebooks/:id/read" element={<EbookReader />} />
            <Route path="/public-library/:id/read" element={<PublicLibraryReader />} />
            <Route path="/teachers/:id" element={<TeacherProfile />} />
            <Route path="/teachers" element={<AllTeachers />} />
            <Route path="/audio-rooms" element={<AudioRooms />} />
            <Route path="/reels" element={<Reels />} />
            <Route path="/notes" element={<AllNotes />} />
            <Route path="/notes/:id" element={<Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div></div>}><NoteDetail /></Suspense>} />
            <Route path="/notes/:id/read" element={<Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div></div>}><NoteReader /></Suspense>} />
            <Route path="/past-papers" element={<AllNotes />} />
            <Route path="/exams" element={<AllExams />} />
            <Route path="/exams/:id" element={<ExamTaking />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/booking/:bookingId/session" element={user ? <BookingSession /> : <Navigate to="/login" />} />
            <Route path="/session/:bookingId" element={user ? <SessionRoom /> : <Navigate to="/login" />} />
            <Route path="/recordings/:recordingId" element={<RecordingPlayback />} />
            <Route path="/r/:shareToken" element={<RecordingPlayback />} />
            <Route path="/dashboard" element={user ? (user.role === 'teacher' ? <Navigate to="/teacher/dashboard" /> : user.role === 'admin' ? <Navigate to="/admin" /> : <Navigate to="/profile" />) : <Navigate to="/login" />} />
            <Route path="/profile" element={user ? (user.role === 'admin' ? <Navigate to="/admin" /> : user.role === 'teacher' ? <Navigate to="/teacher/dashboard" /> : <StudentProfile />) : <Navigate to="/login" />} />
            <Route path="/library" element={user ? <MyLibrary /> : <Navigate to="/login" />} />
            <Route path="/admin/*" element={<AdminDashboard />} />
            <Route path="/teacher/*" element={user?.role === 'teacher' ? <TeacherPortal /> : <Navigate to="/login" />} />
            <Route path="/school-admin/*" element={user ? <SchoolAdminDashboard /> : <Navigate to="/login" />} />
          </Routes>
          </Suspense>
          <SupportChatWidget />
          {notifSettings.activity_popup_enabled && <ActivityPopup />}
          {notifSettings.social_proof_enabled && <SocialProofPopup />}
          {notifSettings.download_popup_enabled && <AppDownloadPopup />}
          {user && notifSettings.session_reminder_enabled && <SessionReminderPopup />}
          {user && <ClassStartAlarm />}
          {user && user.role === 'student' && !user.onboarding_complete && !user.student_level && <StudentOnboarding />}
          <Toaster position="top-right" />
        </BrowserRouter>
      </WebRTCProvider>
      </LanguageProvider>
      </VirtualTeacherContext.Provider>
    </AuthContext.Provider>
  );
}

export default App;